import {
  type ApiClient,
  type AsyncOperation,
  type AsyncOperationAcknowledgement,
  type AuthenticatedApiFetch,
  createOperationScopeSnapshot,
  getOperationScopeKey,
  type OperationKey,
  type OperationReference,
  type OperationScope,
  useApiClient,
  useAuthenticatedApiFetch,
  useTeam,
} from '@vrplatform/data';
import { useCallback, useRef, useState } from 'react';
import { downloadBlob } from '../../utils/download';
import {
  useOperationTrackerLifetimeSignal,
  useTrackOperation,
  useWaitForTrackedOperation,
} from '..';

export type AsyncExportResult = Readonly<{
  blob: Blob;
  fileId: string;
  filename: string;
}>;

export type StartAsyncExport<TBody> = (input: {
  api: ApiClient;
  body: TBody;
  signal: AbortSignal;
}) => Promise<AsyncOperationAcknowledgement>;

type RunAsyncExportInput<TBody> = Readonly<{
  body: TBody;
  exportKey: string;
  fallbackFilename: string;
  start: StartAsyncExport<TBody>;
  transfer?: 'download' | 'none';
}>;

export type AsyncExportRetryState = Readonly<{
  fileId: string | null;
  operationKey: OperationKey;
  reference: OperationReference;
}>;

type PendingAsyncExport = AsyncExportRetryState &
  Readonly<{
    api: ApiClient;
    authenticatedFetch: AuthenticatedApiFetch;
    fallbackFilename: string;
    fileUrl: string | null;
    requestKey: string;
    transfer: 'download' | 'none';
  }>;

const getFilenameFromContentDisposition = (value: string | null) => {
  if (!value) return null;

  const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      return null;
    }
  }

  return /filename\s*=\s*"?([^";]+)"?/i.exec(value)?.[1]?.trim() ?? null;
};

const getStoppedOperationError = (
  status: 'canceled' | 'error' | 'failed' | 'unavailable',
  message: string | null | undefined
) => {
  if (message) return new Error(message);
  if (status === 'failed') return new Error('The export failed.');
  if (status === 'canceled') return new Error('The export was canceled.');
  if (status === 'unavailable') {
    return new Error('The export status is unavailable.');
  }
  return new Error('The export status could not be checked.');
};

const getExportFile = (operation: Pick<AsyncOperation, 'resources'>) => {
  const resultFiles = operation.resources.filter(
    (resource) => resource.role === 'result' && resource.type === 'file'
  );
  if (resultFiles.length !== 1) {
    throw new Error(
      'The completed export did not return exactly one file result.'
    );
  }

  const file = resultFiles[0];
  if (!('url' in file) || typeof file.url !== 'string') {
    throw new Error('The completed export file is missing its download URL.');
  }

  return { fileId: file.id, fileUrl: file.url };
};

const getRequestKey = <TBody>({
  body,
  exportKey,
  fallbackFilename,
  scope,
  transfer,
}: Pick<
  RunAsyncExportInput<TBody>,
  'body' | 'exportKey' | 'fallbackFilename' | 'transfer'
> & { scope: OperationScope }) =>
  JSON.stringify([
    getOperationScopeKey(scope),
    exportKey,
    body,
    fallbackFilename,
    transfer,
  ]);

export const useAsyncExport = () => {
  const api = useApiClient();
  const authenticatedFetch = useAuthenticatedApiFetch();
  const [team] = useTeam();
  const lifetimeSignal = useOperationTrackerLifetimeSignal();
  const trackOperation = useTrackOperation();
  const waitForTrackedOperation = useWaitForTrackedOperation();
  const pendingExportRef = useRef<PendingAsyncExport | null>(null);
  const [retryState, setRetryState] = useState<AsyncExportRetryState | null>(
    null
  );

  const setPendingExport = useCallback((pending: PendingAsyncExport | null) => {
    pendingExportRef.current = pending;
    setRetryState(
      pending
        ? {
            fileId: pending.fileId,
            operationKey: pending.operationKey,
            reference: pending.reference,
          }
        : null
    );
  }, []);

  const resetRetry = useCallback(
    () => setPendingExport(null),
    [setPendingExport]
  );

  const downloadPendingExport = useCallback(
    async (
      pending: PendingAsyncExport,
      signal: AbortSignal
    ): Promise<AsyncExportResult> => {
      let resolvedPending = pending;

      if (!resolvedPending.fileId) {
        const operationResponse = await resolvedPending.api.GET(
          '/operations/{id}',
          {
            headers: {
              'x-data-region': resolvedPending.reference.placement.dataRegion,
              'x-team-id': resolvedPending.reference.requesterTeamId,
            },
            params: {
              path: { id: resolvedPending.reference.operationId },
            },
            signal,
          }
        );
        if (operationResponse.error) throw operationResponse.error;
        if (operationResponse.data.status !== 'completed') {
          throw getStoppedOperationError(
            operationResponse.data.status === 'failed' ? 'failed' : 'error',
            operationResponse.data.failure?.message
          );
        }

        resolvedPending = {
          ...resolvedPending,
          ...getExportFile(operationResponse.data),
        };
        setPendingExport(resolvedPending);
      }

      const fileId = resolvedPending.fileId;
      const fileUrl = resolvedPending.fileUrl;
      if (!fileId || !fileUrl) {
        throw new Error('The completed export is missing its file result.');
      }

      const response = await resolvedPending.authenticatedFetch(
        new Request(fileUrl, { signal })
      );
      if (!response.ok) throw await response.json();
      const blob = await response.blob();
      if (!(blob instanceof Blob)) {
        throw new Error('The export file response was not a blob.');
      }

      const filename =
        getFilenameFromContentDisposition(
          response.headers.get('content-disposition')
        ) ?? resolvedPending.fallbackFilename;
      const result = {
        blob,
        fileId,
        filename,
      };

      if (resolvedPending.transfer === 'download') {
        downloadBlob(result.blob, result.filename);
      }

      setPendingExport(null);
      return result;
    },
    [setPendingExport]
  );

  const retryDownload = useCallback(async (): Promise<AsyncExportResult> => {
    const pending = pendingExportRef.current;
    if (!pending) throw new Error('There is no export download to retry.');

    return downloadPendingExport(pending, lifetimeSignal);
  }, [downloadPendingExport, lifetimeSignal]);

  const runExport = useCallback(
    async <TBody>({
      body,
      exportKey,
      fallbackFilename,
      start,
      transfer = 'download',
    }: RunAsyncExportInput<TBody>): Promise<AsyncExportResult> => {
      const scope = createOperationScopeSnapshot({
        apiTarget: 'regional',
        dataRegion: team.dataRegion,
        regionalApiBaseUrl: team.apiBaseUrl,
        requesterTeamId: team.id,
      });
      const requestKey = getRequestKey({
        body,
        exportKey,
        fallbackFilename,
        scope,
        transfer,
      });
      if (pendingExportRef.current?.requestKey === requestKey) {
        return retryDownload();
      }

      resetRetry();
      const acknowledgement = await start({
        api,
        body,
        signal: lifetimeSignal,
      });
      lifetimeSignal.throwIfAborted();
      const operationKey = trackOperation({
        acknowledgement,
        scope,
        effects: [
          {
            id: `export:${acknowledgement.operationId}:files`,
            kind: 'api',
            target: { scope: 'team', teamIds: [team.id] },
            groups: ['files'],
          },
        ],
      });
      const trackedOperation = await waitForTrackedOperation(operationKey, {
        // The tracker boundary outlives ephemeral export buttons and menus.
        abortOnUnmount: false,
        signal: lifetimeSignal,
      });

      if (
        trackedOperation.status === 'queued' ||
        trackedOperation.status === 'running'
      ) {
        throw new Error('The export tracker stopped before completion.');
      }
      if (trackedOperation.status !== 'completed') {
        throw getStoppedOperationError(
          trackedOperation.status,
          trackedOperation.failure?.message
        );
      }
      if (!('resources' in trackedOperation.operation)) {
        const pending = {
          api,
          authenticatedFetch,
          fallbackFilename,
          fileId: null,
          fileUrl: null,
          operationKey,
          reference: { ...scope, operationId: acknowledgement.operationId },
          requestKey,
          transfer,
        } satisfies PendingAsyncExport;
        setPendingExport(pending);
        throw new Error('The completed export is missing its file result.');
      }

      const pending = {
        api,
        authenticatedFetch,
        fallbackFilename,
        fileId: null,
        fileUrl: null,
        operationKey,
        reference: { ...scope, operationId: acknowledgement.operationId },
        requestKey,
        transfer,
      } satisfies PendingAsyncExport;
      setPendingExport(pending);

      const resolvedPending = {
        ...pending,
        ...getExportFile(trackedOperation.operation),
      } satisfies PendingAsyncExport;
      setPendingExport(resolvedPending);

      return downloadPendingExport(resolvedPending, lifetimeSignal);
    },
    [
      api,
      authenticatedFetch,
      downloadPendingExport,
      lifetimeSignal,
      resetRetry,
      retryDownload,
      setPendingExport,
      team,
      trackOperation,
      waitForTrackedOperation,
    ]
  );

  return { resetRetry, retryDownload, retryState, runExport };
};
